import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import { Alert, AutoComplete, Input, Tooltip } from 'antd';
import { usePartyFlags } from 'lib/Hooks';
import * as yup from 'yup';
import {
  BaseButton,
  ConfirmationModal,
  LoaderOverlay,
  FormField,
  ControlTypes,
  FieldInfo,
  isNil,
  useFilteredRegions,
} from 'vdlib';
import Portlet from 'components/layout/Portlet';
import GarageModal from 'components/common/GarageModal';
import SimpleCollapse from 'components/common/SimpleCollapse';
import { useYupValidator } from 'lib/FormHelper';
import { mapFormDataToApi, useFolderSearching } from './utils';

const defaultFormContext = { updateForm: () => {}, submittedOnce: false };
const getDefaultFormData = ({ auction, folder, garage, party }) => ({
  data: auction || {
    ...folder,
    CountryID: party.CountryID,
  },
  garage: garage || null,
  validationMessages: {},
});

const styles = {
  numberInputError: { borderColor: '#a94442' },
  numberInputIcon: { color: '#ed6b75' },
  garageInputWrap: {
    paddingRight: 0,
    display: 'flex',
    paddingLeft: 8,
  },
  garageInputInner: {
    flexGrow: 1,
  },
  garageInput: {
    backgroundColor: '#FFF',
    paddingLeft: 30,
  },
  garageButton: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    paddingBottom: '0.35rem',
    paddingTop: '0.35rem',
  },
};

/**
 * Creates or edits an auction
 * Cases/scenarios:
 *  - New auction: `auction` and `folder` are undefined
 *  - New auction from existin folder: `auction` undefined, `folder` object
 *  - Edit auction: `auction` and `folder` are objects
 */
const AuctionForm = props => {
  const {
    auction,
    countries,
    creationError,
    folder,
    lang,
    loading,
    onSubmit,
    partTypes,
    partyTypes,
    regions,
    submitting,
    user,
  } = props;
  const { isOperator } = usePartyFlags(user, partyTypes);

  const validationSchema = React.useMemo(() => {
    const lookupEmptyMessage = 'CHOOSE_VALUE';

    const conditionalLookup = isOperator
      ? yup.number().positive().integer().required(lookupEmptyMessage)
      : yup.number().positive().integer(lookupEmptyMessage);

    return yup.object().shape({
      // Folder
      // ExpertID: conditionalLookup,
      // InsuranceID: conditionalLookup,
      Number: yup.number().positive().integer().required(),
      // Title: yup.string().required(),
      PlateNumber: yup.string().required(),
      VehicleBrand: yup.string().required(),
      VehicleModel: yup.string().required(),
      VehicleRegistrationYear: yup
        .number()
        .required()
        .min(1900)
        .max(new Date().getFullYear()),
      VehicleDoors: yup.number().positive().integer(),
      VehicleEngine: yup.string(),
      VehicleEngineCapacity: yup.string(),

      // Part
      PartNumber: yup.string().required(),
      PartDescription: yup.string().required(),
      PartPrice: yup.number().positive().required(),
      // PartTypeID: yup
      //   .number()
      //   .integer()
      //   .positive()
      //   .required(lookupEmptyMessage),
      Quantity: yup.number().integer().positive().required(),
      CountryID: yup.number().integer().positive().required(),
      RegionID: yup.number().integer().positive().required(),
    });
  }, [isOperator]);

  const validate = useYupValidator(validationSchema);

  const [formData, setFormData] = React.useState(getDefaultFormData(props));
  const [showGarageModal, setShowGarageModal] = React.useState(false);

  const formContext = React.useRef(defaultFormContext);
  React.useEffect(() => {
    formContext.current.submittedOnce = false;
  }, []);

  const filteredRegions = useFilteredRegions(
    regions,
    countries,
    formData?.data?.CountryID
  );

  const { partFields, folderFields } = React.useMemo(() => {
    const fieldMapper = f => ({ ...f, formContext: formContext.current });

    const folderFields = [
      //new FieldInfo('Number', I.t('FOLDER_NO'), ''),
      // new FieldInfo('Title', I.t('AUCTION_TITLE'), ''),
      new FieldInfo('PlateNumber', I.t('PLATE_NUMBER'), ''),
      new FieldInfo('VehicleBrand', I.t('BRAND'), ''),
      new FieldInfo('VehicleModel', I.t('MODEL'), ''),
      new FieldInfo('VehicleRegistrationYear', I.t('REGISTRATION_YEAR'), ''),
      new FieldInfo('VehicleDoors', I.t('DOORS'), ''),
      new FieldInfo('VehicleEngine', I.t('ENGINE_TYPE'), ''),
      new FieldInfo('VehicleEngineCapacity', I.t('ENGINE_CAPACITY'), ''),
      // new FieldInfo('FuelTypeID', 'Καύσιμο', 1, this.props.fuelTypes, null, ControlTypes.Dropdown),
    ].map(fieldMapper);

    const partFields = [
      // new FieldInfo('InsuranceID', 'Ασφαλιστική', 1, expertInsurances, null, ControlTypes.Dropdown),
      // new FieldInfo('ExpertID', 'Εμπειρογνώμονας', 1, insuranceExperts, null, ControlTypes.Dropdown),
      new FieldInfo('PartNumber', I.t('PART_NUMBER'), ''),
      new FieldInfo('PartDescription', I.t('DESCRIPTION'), ''),
      new FieldInfo(
        'PartPrice',
        I.t('PRICE'),
        0,
        null,
        null,
        ControlTypes.Currency
      ),
      // new FieldInfo(
      //   'PartTypeID',
      //   I.t('PART_TYPE_LONG'),
      //   1,
      //   partTypes,
      //   null,
      //   ControlTypes.Dropdown
      // ),
      new FieldInfo('Quantity', I.t('QUANTITY'), ''),
      // new FieldInfo('StartDate', 'Ημερ.Έναρξης', ''),
      // new FieldInfo('PeriodDays', 'Διάρκεια', ''),
      // new FieldInfo('StatusID', 'Κατάσταση', 67, this.props.auctionStatuses, null, ControlTypes.Dropdown),
      new FieldInfo(
        'CountryID',
        I.t('COUNTRY'),
        67,
        countries,
        null,
        ControlTypes.Dropdown
      ),
      new FieldInfo(
        'RegionID',
        I.t('REGION'),
        1,
        filteredRegions,
        null,
        ControlTypes.Dropdown
      ),
    ].map(fieldMapper);

    return {
      partFields,
      folderFields,
    };
  }, [countries, lang, partTypes, filteredRegions]); //eslint-disable-line

  // if editing and folder changed, display message. Not applicable if creating from folder
  const folderHasDiff = React.useMemo(
    () =>
      !folder &&
      auction &&
      folderFields.some(
        ({ name: fieldName }) =>
          auction[fieldName] !== formData?.data[fieldName]
      ),
    [folder, folderFields, auction, formData]
  );

  const handleUpdateForm = React.useCallback(
    (key, value) => {
      var msg = validate(key, value);

      setFormData(fd => ({
        data: { ...fd.data, [key]: value },
        garage: fd.garage,
        validationMessages: {
          ...fd.validationMessages,
          [key]: msg == null || isNil(msg[key]) ? null : msg[key],
        },
      }));
    },
    [validate, setFormData]
  );

  const handleSubmit = React.useCallback(
    e => {
      if (e.preventDefault) e.preventDefault();

      const validationResult = validate(formData.data);
      const invalidKeys = Object.keys(validationResult);
      if (formData.garage && invalidKeys.length === 0) {
        const payload = mapFormDataToApi(formData);
        onSubmit(payload);
      } else {
        formContext.current.submittedOnce = true;
        setFormData(fd => ({ ...fd, validationMessages: validationResult }));
      }

      return false;
    },
    [validate, formData, onSubmit]
  );

  const handleSaveGarage = React.useCallback(
    garage => {
      setFormData(fd => ({ ...fd, garage }));
      setShowGarageModal(false);
    },
    [setFormData, setShowGarageModal]
  );

  // Sync form context ref updater
  React.useEffect(() => {
    formContext.current.updateForm = handleUpdateForm;
  }, [handleUpdateForm]);

  // on filtered regions change, select the first region, if existing
  React.useEffect(() => {
    if (filteredRegions && filteredRegions.length) {
      setFormData(fd => {
        const existing = filteredRegions.find(r => r.ID === fd.data.RegionID);
        if (existing) return fd;

        const original =
          auction && filteredRegions.find(r => r.ID === auction.RegionID);

        return {
          ...fd,
          data: { ...fd.data, RegionID: original?.ID || filteredRegions[0].ID },
        };
      });
    }
  }, [auction, filteredRegions]); //eslint-disable-line

  const globalValidationMessage = formData.validationMessages[''] || '';

  const {
    folderOptions,
    folderSearchTerm,
    handleResetConfirmation,
    handleSearch,
    handleSearchChange,
    handleSearchKeyDown,
    handleSelect,
    setFolderSearchTerm,
    setShowResetModal,
    showResetModal,
  } = useFolderSearching(props, setFormData);

  const numberHasError = !!formData.validationMessages?.Number;
  const garageHasError = formContext.current.submittedOnce && !formData.garage;

  return (
    <>
      {creationError && (
        <div className="row alert alert-danger">
          {creationError.message || String(creationError)}
        </div>
      )}

      <form action="#" onSubmit={handleSubmit} className="form-horizontal">
        <LoaderOverlay loading={submitting} />
        <div className="form-body">
          <Portlet
            showBackButton={!!auction || !!folder}
            title={I.t('FOLDER_C')}
            titleColorClass="font-grey-salsa"
          >
            <div className="row">
              <div className="col-md-6">
                <div className="form-group row">
                  <label class="control-label col-md-3">Αρ.Φακέλου</label>
                  <div className={`col-md-9 ${numberHasError && 'has-error'}`}>
                    <AutoComplete
                      disabled={!!auction || !!folder}
                      onChange={setFolderSearchTerm}
                      onSelect={handleSelect}
                      onSearch={handleSearch}
                      options={folderOptions}
                      style={{ width: '100%' }}
                      value={folderSearchTerm}
                    >
                      <Input
                        allowClear
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        size="large"
                        style={numberHasError && styles.numberInputError}
                        suffix={
                          numberHasError && (
                            <Tooltip
                              placement="left"
                              title={formData.validationMessages?.Number}
                            >
                              <i
                                className="fa fa-warning"
                                style={styles.numberInputIcon}
                              />
                            </Tooltip>
                          )
                        }
                      />
                    </AutoComplete>
                  </div>
                </div>
              </div>
              {folderFields.map(f => (
                <FormField
                  disabled={!!folder}
                  field={f}
                  key={f.name}
                  value={formData.data[f.name]}
                  validationMessage={formData.validationMessages[f.name]}
                />
              ))}
            </div>
            <div>
              <SimpleCollapse in={folderHasDiff}>
                <Alert
                  type="warning"
                  description="Τα στοιχεία φακέλου θα αλλάξουν για όλα τα ανταλλακτικά στο
                  φάκελο"
                  message="Προσοχή"
                />
              </SimpleCollapse>
            </div>
          </Portlet>
          <Portlet title={I.t('PART_C')} titleColorClass="font-grey-salsa">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group input-group">
                  <label class="control-label col-md-3">Συνεργείο</label>
                  <div
                    className={`col-md-9 ${garageHasError && 'has-error'}`}
                    style={styles.garageInputWrap}
                  >
                    <div style={styles.garageInputInner}>
                      <div className="input-icon">
                        <i className="fa fa-wrench" style={{ color: '#CCC' }} />
                      </div>
                      {garageHasError && (
                        <div className="input-icon right">
                          <i className="fa fa-warning"></i>
                        </div>
                      )}
                      <input
                        readOnly={true}
                        className="form-control form-control-sm has-error"
                        type="text"
                        style={styles.garageInput}
                        value={(formData.garage || {}).DistinctiveTitle}
                      />
                    </div>
                    <span className="input-group-btn">
                      <BaseButton
                        className="btn-primary"
                        disabled={loading}
                        onClick={() => setShowGarageModal(true)}
                        style={styles.garageButton}
                      >
                        {I.t('SELECT')}
                      </BaseButton>
                    </span>
                  </div>
                </div>
              </div>
              {partFields.map(f => (
                <FormField
                  key={f.name}
                  field={f}
                  value={formData.data[f.name]}
                  validationMessage={formData.validationMessages[f.name]}
                />
              ))}
            </div>
          </Portlet>
        </div>
        <div className="row form-actions mb">
          <div className="col-md-12">
            <BaseButton type="submit" className="btn btn-sm green float-right">
              {I.t('SAVE')}
            </BaseButton>
          </div>
        </div>
        {globalValidationMessage && (
          <div className="row alert alert-danger">
            {globalValidationMessage}
          </div>
        )}
      </form>

      <GarageModal
        loading={submitting}
        allowCreation={true}
        onHide={() => setShowGarageModal(false)}
        show={showGarageModal}
        garageData={formData.garage}
        onSubmit={handleSaveGarage}
      />

      <ConfirmationModal
        cancelButton={I.t('CANCEL')}
        promptText="Τα στοιχεία του φακέλου θα καθαρίσουν. Η εισαγωγή δεδομένων φακέλου θα ξεκινήσει από την αρχή."
        onHide={() => setShowResetModal(false)}
        onSubmit={handleResetConfirmation}
        rejectButton={false}
        show={showResetModal}
        submitButton={I.t('OK')}
      />
    </>
  );
};

AuctionForm.propTypes = {
  auction: object,
  clearNewAuctionGarage: func,
  countries: array,
  creationError: object,
  folder: object,
  folders: array,
  folderSearchError: object,
  folderSearchPending: bool,
  garage: object,
  lang: string,
  loading: bool,
  onClearSearchFolders: func,
  onSearchFolders: func,
  onSubmit: func,
  partTypes: array,
  partyTypes: array,
  regions: array,
  submitting: bool,
  user: object,
};

export default AuctionForm;
